import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  output,
  Signal,
  signal,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EmailManagementApiService } from '@anduin/api';
import { ConfigService } from '@anduin/config';
import { EmailDeliveryStep, EmailDeliveryTrackService } from '../../tracking';
import {
  AgGridFilterAggregate,
  AgGridFilterModel,
  AgGridStateService,
  distinctUntilObjectChanged,
  getLoadedResults,
  ignoreNullish,
  NULL_DATA_STRING,
  showOverlayOnZeroState,
} from '@anduin/shared';
import { Store } from '@ngxs/store';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, ColGroupDef, ColumnApi, GridApi, GridOptions, GridReadyEvent } from 'ag-grid-community';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, take, tap } from 'rxjs';
import { DEFAULT_SNACKBAR_CONFIG, SnackbarComponent, SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { EmailDeliveryHubState, LoadMetrics } from '../../state';
import { ResendEmailModalComponent } from '../resend-email-modal/resend-email-modal.component';
import { ViewAttachmentsDialogComponent, ViewAttachmentsDialogData } from '../view-attachments-dialog/view-attachments-dialog.component';
import { EmailDeliveryHubColumnsFactory, EmailDeliveryHubGridOptionsFactory } from './factories';
import { EmailDeliveryHubExportConfig } from './email-delivery-hub-export-config';
import {
  EmailDeliveryHubAgGridFilterAggregateFactory,
  EmailDeliveryHubDataSourceService,
  EmailDeliveryHubExportDataSource,
} from './services';
import {
  EMAIL_DELIVERY_HUB_HASURA_BASE_QUERY_TOKEN,
  EMAIL_DELIVERY_HUB_TABLE_SEARCHABLE_COLUMNS,
  getSentTableExportParams,
  SENT_AT_COLUMN_ID,
} from './utils';
import { delay } from 'rxjs/operators';
import { EmailDeliveryHubSentRefreshService } from '../../services';
import { EmailDeliveryHubSentFilters, getEnabledEmailTypes, OutboundEmailRow } from '../../models';
import { buildRequestSnapshot, TableRequestSnapshot } from '../../utils';

type RecipientType = 'to' | 'cc' | 'bcc';

interface Recipient {
  type: RecipientType;
  email: string;
}

@Component({
  selector: 'app-email-delivery-hub-sent-table',
  templateUrl: './email-delivery-hub-sent-table.component.html',
  styleUrls: ['./email-delivery-hub-sent-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: { class: 'flex-fill-container-y' },
})
export class EmailDeliveryHubSentTableComponent implements OnInit {
  private readonly agGridFilterAggregateFactory = inject(EmailDeliveryHubAgGridFilterAggregateFactory);
  private readonly gridOptionsFactory = inject(EmailDeliveryHubGridOptionsFactory);
  private readonly gridColumnsFactory = inject(EmailDeliveryHubColumnsFactory);
  protected readonly datasource = inject(EmailDeliveryHubDataSourceService);
  private readonly apollo = inject(Apollo);
  private readonly exportBaseQuery = inject(EMAIL_DELIVERY_HUB_HASURA_BASE_QUERY_TOKEN);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly store = inject(Store);
  private readonly sentRefreshService = inject(EmailDeliveryHubSentRefreshService);
  private readonly emailManagementApi = inject(EmailManagementApiService);
  private readonly configService = inject(ConfigService);
  private readonly trackService = inject(EmailDeliveryTrackService);
  private readonly viewContainerRef = inject(ViewContainerRef);

  @ViewChild(AgGridAngular) private agGrid: AgGridAngular;

  private readonly statusTemplateRef: Signal<TemplateRef<HTMLElement>> = viewChild('statusTemplate');
  private readonly booleanIconTemplateRef: Signal<TemplateRef<HTMLElement>> = viewChild('booleanIconTemplate');
  private readonly bounceReasonTemplateRef: Signal<TemplateRef<HTMLElement>> = viewChild('bounceReasonTemplate');

  readonly gridApiReady = output<GridApi>();
  readonly exportConfigReady = output<EmailDeliveryHubExportConfig>();

  private gridApi$ = new BehaviorSubject<GridApi>(undefined);
  private agGridStateService: AgGridStateService;
  private datasourceSet = false;

  protected readonly exportGridApi = signal<GridApi | undefined>(undefined);
  protected readonly exportColumnApi = signal<ColumnApi | undefined>(undefined);
  protected readonly getExportParams = getSentTableExportParams;
  protected exportGridOptions: GridOptions;
  protected exportColumnDefs: (ColDef | ColGroupDef)[];
  protected readonly createExportDatasource = (): EmailDeliveryHubExportDataSource =>
    new EmailDeliveryHubExportDataSource(this.apollo, this.exportBaseQuery, this._createExportRequestSnapshot());

  private filtersLoadable$ = this.store.select(EmailDeliveryHubState.getFiltersLoadable);
  private filters$ = getLoadedResults(this.filtersLoadable$);

  public columnDefs: (ColDef | ColGroupDef)[];
  public gridOptions: GridOptions;
  public NULL_DATA_STRING = NULL_DATA_STRING;

  ngOnInit(): void {
    this.onExternalFilterChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.updateMetricsOnFilterChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    this.columnDefs = this.gridColumnsFactory.create({
      onResendClick: this.onResendClick.bind(this),
      onViewAttachmentsClick: this.onViewAttachmentsClick.bind(this),
      booleanTemplateRef: this.booleanIconTemplateRef(),
      statusTemplateRef: this.statusTemplateRef(),
      bounceReasonTemplateRef: this.bounceReasonTemplateRef(),
    });

    this.gridOptions = this.gridOptionsFactory.create({
      onGridReadyCallback: this.onGridReady.bind(this),
      getGridDataStatus: () => this.datasource.requestStatus,
      isExternalFilterPresent: () => this.datasource.externalFilters.size > 0,
    });

    this.exportGridOptions = this.gridOptionsFactory.createExportGridOptions();
    this.exportColumnDefs = [this.gridOptionsFactory.createExportSentAtColumnDef(), ...this.columnDefs];

    // onModelUpdated doesn't seem to work with server side row model, so we need to manually hook up data state changes
    this.datasource.requestStatus$.pipe(
      delay(250), // Delay to allow grid to update rows first
      tap(() => showOverlayOnZeroState(this.gridApi$.value)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi$.next(params.api);
    this.exportGridApi.set(params.api);
    this.exportColumnApi.set(params.columnApi);
    this.gridApiReady.emit(params.api);
    this.exportConfigReady.emit({
      tableId: 'emailDeliveryHubSent',
      gridApi: params.api,
      columnApi: params.columnApi,
      defaultColumnDef: this.exportGridOptions.defaultColDef,
      columnDefs: this.exportColumnDefs,
      gridOptions: this.exportGridOptions,
      externalFilters: this.datasource.externalFilters,
      createDatasource: this.createExportDatasource,
      getExportParams: this.getExportParams,
    });
    this.sentRefreshService.setGridApi(params.api);

    this.agGridStateService = new AgGridStateService(
      { tableName: 'emailDeliveryHubSent', rememberFilterModel: true },
      this.store,
      this.agGrid,
    );
    this.agGridStateService
      .getAllEventListeners()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();

    this._dropUnavailableEmailTypeFilterValues(params.api);
  }

  /**
   * Drops stale email-type values from the restored column filter when a feature gate has
   * been turned off between sessions. Re-applying the filter model after sanitization
   * triggers `filterChanged`, which persists the cleaned state via `AgGridStateService`.
   */
  private _dropUnavailableEmailTypeFilterValues(api: GridApi): void {
    const filterModel = api.getFilterModel();
    const emailTypeFilter = filterModel?.emailType;
    const restoredValues: string[] | undefined = emailTypeFilter?.filter;
    if (!restoredValues?.length) {
      return;
    }

    const enabledTypes = new Set(getEnabledEmailTypes(this.configService));
    const retained = restoredValues.filter((value) => enabledTypes.has(value));
    if (retained.length === restoredValues.length) {
      return;
    }

    if (retained.length === 0) {
      const remaining = { ...filterModel };
      delete remaining.emailType;
      api.setFilterModel(Object.keys(remaining).length ? remaining : null);
    } else {
      api.setFilterModel({
        ...filterModel,
        emailType: { ...emailTypeFilter, filter: retained },
      });
    }
  }

  async onResendClick(model: OutboundEmailRow): Promise<void> {
    const trackingIds = model.id
      ? { outboundEmailV4Id: model.id }
      : { outboundEmailId: model.outboundEmailId };
    this.trackService.trackWorkflow(EmailDeliveryStep.ROW_ACTION_CLICKED, {
      action: 'RESEND',
      ...trackingIds,
      emailType: model.emailType,
    });

    const { allowed, emailSettingDisplay } = await firstValueFrom(this.emailManagementApi.checkRecipientEditAllowed(trackingIds));

    const dialogRef = this.dialog.open(ResendEmailModalComponent, {
      data: {
        mode: 'resend' as const,
        recipients: [
          ...this._getRecipients(model.recipients, 'to'),
          ...this._getRecipients(model.cc, 'cc'),
          ...this._getRecipients(model.bcc, 'bcc'),
        ],
        model: model,
        canEditRecipients: allowed,
        emailSettingDisplay,
        tracking: { ...trackingIds, emailType: model.emailType },
      },
      viewContainerRef: this.viewContainerRef,
    });

    dialogRef
      .afterClosed()
      .pipe(
        take(1),
        filter((result) => result?.success === true),
        tap(() => {
          // Show success snackbar
          this.snackBar.openFromComponent(SnackbarComponent, {
            ...DEFAULT_SNACKBAR_CONFIG,
            data: {
              message: 'Email resent successfully. The email will be available to view after the data has been refreshed.',
              type: SnackbarType.SUCCESS,
            },
          });
        }),
      )
      .subscribe();
  }

  onViewAttachmentsClick(model: OutboundEmailRow): void {
    this.dialog.open(ViewAttachmentsDialogComponent, {
      data: { model } as ViewAttachmentsDialogData,
      viewContainerRef: this.viewContainerRef,
    });
  }

  onExternalFilterChange$ = combineLatest([
    this.gridApi$.pipe(ignoreNullish()),
    this.filters$.pipe(
      distinctUntilObjectChanged(),
    ),
  ]).pipe(
    tap(([, edhFilters]) => this._addSearchFilterToDataSourceIfPresent(edhFilters)),
    map(
      ([gridApi, filters]) =>
        [
          gridApi,
          this.agGridFilterAggregateFactory.create(filters),
          this.agGridFilterAggregateFactory.createTypeFilter(filters)
        ] as [
          GridApi,
          AgGridFilterAggregate,
          AgGridFilterModel,
        ],
    ),
    tap(([, filters]) => this._addFiltersToDatasource(filters)),
    tap(([, _, typeFilters]) => this._addTypeFiltersToDatasource(typeFilters)),
    tap(([gridApi]) => this._setDatasourceOrRefreshGrid(gridApi)),
  );

  updateMetricsOnFilterChange$ = combineLatest([
    this.gridApi$.pipe(ignoreNullish()),
    this.filters$.pipe(
      // temporary transition period until we remove the deprecated min/max fields in favor of dateRange
      map(({ min, max, dateRange, types }) => ({ min: dateRange?.min ?? min, max: dateRange?.max ?? max, types: types })),
      distinctUntilObjectChanged(),
    ),
  ]).pipe(
    tap(() => this.store.dispatch(new LoadMetrics())),
  );

  private _addSearchFilterToDataSourceIfPresent(edhFilters: Partial<EmailDeliveryHubSentFilters>): void {
    if (!edhFilters.search) {
      this.datasource.removeExternalFilter('searchTerm');
      return;
    }

    this.datasource.addSearchBarExternalFilter(
      'searchTerm',
      EMAIL_DELIVERY_HUB_TABLE_SEARCHABLE_COLUMNS,
      'text',
      'contains',
      edhFilters.search,
    );
  }

  private _addFiltersToDatasource(filters?: AgGridFilterAggregate): void {
    if (!filters) {
      this.datasource.removeExternalFilter('filters');
      return;
    }

    this.datasource.addExternalFilter('filters', filters);
  }

  private _addTypeFiltersToDatasource(filters: AgGridFilterModel): void {
    if (!filters) {
      this.datasource.removeExternalFilter('typeFilter');
      return;
    }

    this.datasource.addExternalFilter('typeFilter', filters);
  }

  private _setDatasourceOrRefreshGrid(gridApi: GridApi): void {
    if (!this.datasourceSet) {
      // First time: set datasource (automatically fetches with filters already applied)
      gridApi.setServerSideDatasource(this.datasource);
      this.datasourceSet = true;
    } else {
      // Subsequent times: refresh to fetch updated data
      gridApi.refreshServerSide({ purge: true });
    }
  }

  private _createExportRequestSnapshot(): TableRequestSnapshot {
    return buildRequestSnapshot(this.exportGridApi(), this.exportColumnApi(), (colId) =>
      colId === 'ag-Grid-AutoColumn' ? SENT_AT_COLUMN_ID : colId,
    );
  }

  private _getRecipients<Type extends RecipientType>(recipientString: string | undefined, type: Type): Recipient[] {
    if (!recipientString?.trim()) return [];

    return recipientString
      .replace(/[[\]"]+/g, '')
      .split(',')
      .map((r) => r.trim())
      .filter(Boolean)
      .map((email) => ({ type, email }));
  }
}
